/**
 * cutReasons — plain-language reasons for items below the cut line.
 *
 * Product laws this module exists to serve:
 *   L4 "the cutline is the product" — every item below the line deserves an
 *       honest, legible answer to "why not this one."
 *   L1 "a dishonest plan is worse than no plan" — never invent a specific
 *       cause (a blocking item's name, a rule conflict, an exact capacity
 *       gap) when the caller hasn't actually supplied the data behind it.
 *       When the basis is unknown, say so plainly instead of guessing.
 *
 * This module is a PURE library: no React, no Supabase, no Zustand stores,
 * no side effects. Every fact it reasons about arrives as a plain argument.
 * It is intentionally decoupled from the planning-engine's internal types
 * (PrioritySummary, PlanOfRecord, etc.) — see CutReasonInput below — so it
 * can be called from anywhere (a Vite page, an edge function, a script)
 * without dragging in engine or store code.
 *
 * Semantics are ported from the B-mode engine workbench's
 * `reasonForBelowLine()` in src/pages/planning/engine/AboveBelowLineView.tsx
 * (read-only reference — not imported), generalized to also cover:
 *   - relationship-rule violations (Package / Competing / Cluster conflicts),
 *     which product ruling treats the same as an unmet dependency: they make
 *     an item not-achievable, not merely "risky" (see
 *     project_relationship_rules_in_planning notes).
 *   - explicit human deprioritization, distinct from a computed constraint.
 *   - the sparse case, where only a cycle report's plan_of_record jsonb is
 *     available (ranked_items + cut_line_position — see
 *     src/components/prioritization/CycleReportView.tsx ~L225-226, 383-433
 *     for the shape this is read-only-referenced against). In that case we
 *     never fabricate a specific cause — we say where it ranked and that
 *     capacity ran out above it, and nothing more.
 *
 * Plain-language rule (binding): no jargon. Never say "loop", "POR", "S2",
 * "RPC", "feasibility vector", or similar internal vocabulary in a reason
 * string — a middle-schooler's principal should understand every sentence.
 */

// ─── Public types ───────────────────────────────────────────────────────────

export type CutReasonKind =
  | 'capacity'
  | 'dependency'
  | 'rule_violation'
  | 'deprioritized'
  | 'unknown';

export interface CutReason {
  kind: CutReasonKind;
  reason: string;
}

/**
 * A relationship-rule conflict (Package / Competing / Cluster) that makes an
 * item not-achievable alongside another item already in the plan. Only the
 * fields the caller actually knows should be supplied — an absent
 * `withItemName` degrades to a generic (but still specific-in-kind) message
 * rather than inventing a name.
 */
export interface CutReasonRuleViolation {
  withItemName?: string | null;
}

/**
 * Input to reasonForBelowLine(). Tolerates two very different data
 * situations without the caller needing to know which one it's in:
 *
 *  - "Rich" mode: engine/feasibility data is available (per-item feasibility
 *    flags, a blocking dependency's name, a relationship-rule conflict's
 *    name, a capacity-exhaustion point). Populate whichever of the rich
 *    fields you have — partial rich data still produces a specific-kind
 *    (if less specific-in-wording) reason.
 *
 *  - "Sparse" mode: only a rank and a total item count are known (e.g. from
 *    a cycle report's plan_of_record.ranked_items + cut_line_position).
 *    Supply just `rank` and `totalItems` and this returns the honest
 *    generic — it will never guess at capacity, a dependency, or a rule
 *    conflict from rank data alone.
 *
 * All fields are optional and independently nullable — pass only what you
 * actually know. Malformed values (wrong type, negative counts, etc.) are
 * ignored rather than thrown on.
 */
export interface CutReasonInput {
  /** Optional identity — not required for reasoning, useful for callers. */
  itemId?: string | null;
  itemName?: string | null;

  /**
   * When explicitly known and true, this item is ABOVE the cut line. Below
   * the cut is the default assumption when this is omitted, since callers
   * generally only invoke this for items they already know are below the
   * line — but passing `true` here is honored and produces a clearly
   * distinguished, non-committal result rather than a fabricated reason.
   */
  aboveCutLine?: boolean | null;

  // ── Sparse-mode fields ──────────────────────────────────────────────────
  /** This item's 1-based rank in the ranked list. */
  rank?: number | null;
  /** Total number of items considered in the ranked list. */
  totalItems?: number | null;

  // ── Rich-mode fields ─────────────────────────────────────────────────────
  /**
   * Mirrors the engine's PriorityFeasibility, generalized to plain string
   * literals so this module never needs to import the engine's types.
   */
  feasibility?: 'fully_achievable' | 'partially_achievable' | 'not_achievable' | 'no_work' | null;

  /** True when this item is waiting on a hard dependency that isn't in the plan yet. */
  hasUnmetDependency?: boolean | null;
  /** The specific item this one is blocked on, when known. */
  blockedByItemName?: string | null;

  /** True when this item was cut purely because the budget ran out. */
  insufficientCapacity?: boolean | null;
  /** How many ranks above this one the capacity budget was exhausted, when known. */
  capacityRanksAbove?: number | null;

  /** A relationship-rule (Package / Competing / Cluster) conflict, when one exists. */
  ruleViolation?: CutReasonRuleViolation | null;

  /** True when a human deliberately left this out, independent of computed feasibility. */
  manuallyDeprioritized?: boolean | null;
  /** A short human-supplied note explaining the deprioritization, when known. */
  deprioritizedNote?: string | null;
}

// ─── Internal helpers ───────────────────────────────────────────────────────

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function safeInput(input: CutReasonInput | null | undefined): CutReasonInput {
  return input && typeof input === 'object' ? input : {};
}

const GENERIC_UNKNOWN: CutReason = {
  kind: 'unknown',
  reason: "Below the cut line — the plan didn't have room for this item.",
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Returns a plain-language reason a single item is below the cut line.
 *
 * Never throws: malformed or missing fields degrade to the honest generic
 * (GENERIC_UNKNOWN) rather than raising. Never fabricates a specific cause
 * (a name, a count) that wasn't supplied in `input`.
 */
export function reasonForBelowLine(input: CutReasonInput): CutReason {
  try {
    const s = safeInput(input);

    // An item explicitly known to be above the line gets a clearly
    // distinguished, non-committal result — it is never given a below-the-
    // line reason.
    if (s.aboveCutLine === true) {
      return {
        kind: 'unknown',
        reason: 'This item is above the cut line — it does not need a reason.',
      };
    }

    // Relationship-rule conflicts (Package / Competing / Cluster) make an
    // item not-achievable the same way an unmet dependency does — surface
    // them first since they name a specific, resolvable cause.
    if (s.ruleViolation && typeof s.ruleViolation === 'object') {
      const withName = isNonEmptyString(s.ruleViolation.withItemName)
        ? s.ruleViolation.withItemName.trim()
        : null;
      return {
        kind: 'rule_violation',
        reason: withName
          ? `Conflicts with ${withName} — the two can't both run.`
          : "Conflicts with another item in the plan — the two can't both run.",
      };
    }

    // Blocked on a dependency.
    if (s.hasUnmetDependency === true || isNonEmptyString(s.blockedByItemName)) {
      const blockedBy = isNonEmptyString(s.blockedByItemName) ? s.blockedByItemName.trim() : null;
      return {
        kind: 'dependency',
        reason: blockedBy
          ? `Blocked — waiting on ${blockedBy}.`
          : 'Blocked — waiting on another item to finish first.',
      };
    }

    // Cut for lack of capacity.
    if (s.insufficientCapacity === true || isFiniteNumber(s.capacityRanksAbove)) {
      const ranksAbove = isFiniteNumber(s.capacityRanksAbove) && s.capacityRanksAbove > 0
        ? Math.round(s.capacityRanksAbove)
        : null;
      return {
        kind: 'capacity',
        reason: ranksAbove
          ? `Not enough capacity — the budget runs out ${ranksAbove} item${ranksAbove === 1 ? '' : 's'} above this one.`
          : 'Not enough capacity — the budget ran out before reaching this item.',
      };
    }

    // A deliberate human call, independent of any computed constraint.
    if (s.manuallyDeprioritized === true) {
      const note = isNonEmptyString(s.deprioritizedNote) ? s.deprioritizedNote.trim() : null;
      return {
        kind: 'deprioritized',
        reason: note
          ? `Set aside — ${note}`
          : 'Set aside by a deliberate call, not a capacity or dependency limit.',
      };
    }

    // Rich data says there is simply no work defined to schedule.
    if (s.feasibility === 'no_work') {
      return {
        kind: 'unknown',
        reason: "No work has been defined for this item yet, so it can't be scheduled.",
      };
    }

    // Sparse mode: only a rank and total are known. Say where it ranked and
    // that capacity ran out above it — nothing more specific than that.
    if (isFiniteNumber(s.rank) && s.rank > 0 && isFiniteNumber(s.totalItems) && s.totalItems > 0) {
      const rank = Math.round(s.rank);
      const total = Math.round(s.totalItems);
      return {
        kind: 'unknown',
        reason: `Below the cut — ranked #${rank} of ${total}; the plan's capacity ran out above this line.`,
      };
    }

    // Nothing usable was supplied — the fully honest generic.
    return GENERIC_UNKNOWN;
  } catch {
    // Defense in depth: a reason string should never be able to crash a
    // caller, even if `input` turns out to be a hostile or unusual object.
    return GENERIC_UNKNOWN;
  }
}

/**
 * A single ranked item's shape as it appears in a cycle report's
 * plan_of_record jsonb column (Brief G §2). Deliberately a local,
 * structurally-typed mirror — not an import — of
 * src/stores/prioritization/cycleReportStore.ts's PlanOfRecord, so this
 * module has no runtime or type dependency on store code and stays
 * tolerant of any jsonb drift.
 */
interface RankedItemLike {
  item_id?: unknown;
  rank?: unknown;
  title?: unknown;
  above_cut_line?: unknown;
}

interface PlanOfRecordLike {
  ranked_items?: unknown;
  total_items_considered?: unknown;
}

/**
 * Walks a plan_of_record-shaped payload (see CycleReportView.tsx ~L225-226,
 * 383-433 for the reference shape) and returns a plain-language reason for
 * every below-cut item. An item is treated as below the cut whenever
 * `above_cut_line` is not exactly `true` — matching CycleReportView's own
 * `ranked_items.filter(i => !i.above_cut_line)`. Above-cut items are simply
 * absent from the returned map.
 *
 * Sparse by default (rank + total only). Pass `richDataByItemId` to merge in
 * per-item engine/feasibility data — any rich fields you supply there take
 * priority over the sparse rank-based fallback for that item.
 *
 * Never throws: any malformed payload yields an empty map.
 */
export function reasonsForReport(
  reportPayload: unknown,
  richDataByItemId?: Record<string, Partial<CutReasonInput>> | Map<string, Partial<CutReasonInput>> | null,
): Map<string, CutReason> {
  const result = new Map<string, CutReason>();

  try {
    if (!reportPayload || typeof reportPayload !== 'object') return result;
    const payload = reportPayload as PlanOfRecordLike;
    const items = payload.ranked_items;
    if (!Array.isArray(items)) return result;

    const total = isFiniteNumber(payload.total_items_considered) && payload.total_items_considered > 0
      ? payload.total_items_considered
      : items.length;

    const richFor = (itemId: string): Partial<CutReasonInput> | undefined => {
      if (!richDataByItemId) return undefined;
      if (richDataByItemId instanceof Map) return richDataByItemId.get(itemId);
      return richDataByItemId[itemId];
    };

    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as RankedItemLike;

      const itemId = typeof item.item_id === 'string' && item.item_id.length > 0 ? item.item_id : null;
      if (!itemId) continue;

      // Matches CycleReportView's belowCut filter: !i.above_cut_line.
      if (item.above_cut_line === true) continue;

      const rank = isFiniteNumber(item.rank) ? item.rank : null;
      const itemName = typeof item.title === 'string' ? item.title : null;

      const reason = reasonForBelowLine({
        itemId,
        itemName,
        aboveCutLine: false,
        rank,
        totalItems: total,
        ...richFor(itemId),
      });

      result.set(itemId, reason);
    }
  } catch {
    // Never throw — an empty map is the honest fallback for an
    // unrecognizable payload.
    return new Map();
  }

  return result;
}
